import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Briefcase, CalendarClock, CheckCircle2, Wallet, XCircle, Zap, ShieldAlert, Clock, ArrowLeft, RefreshCw, Phone, Mail, Lock } from "lucide-react";
import { toast } from "react-hot-toast"; 

const Backdrop = ({ children, onClose }) => (
  <div className="fixed inset-0 z-40 flex items-center justify-center">
    {/* blurred background */}
    <div
      className="absolute inset-0 bg-black/30 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-50 w-full max-w-2xl p-6">{children}</div>
  </div>
);

const PendingReviewModal = ({ name, onClose }) => {
  return (
    <Backdrop onClose={onClose}>
      <div className="rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-2xl font-semibold">Welcome back, {name}</h3>
        <p className="mt-3 text-gray-600">
          Your profile is currently under review. You will be able to access the
          dashboard once your profile is approved and activated. In the
          meantime, please stay in touch. If you need assistance, contact our
          support team at:
        </p>
        <ul className="mt-3 list-disc list-inside text-gray-600">
          <li>Support: +92 3039672114</li>
          <li>Email: support@example.com</li>
        </ul>
        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className="rounded px-4 py-2 bg-gray-100 hover:bg-gray-200"
          >
            Dismiss
          </button>
        </div>
      </div>
    </Backdrop>
  );
};

const FinalApprovalPendingModal = ({ name, onClose }) => {
  return (
    <Backdrop onClose={onClose}>
      <div className="rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-2xl font-semibold text-gray-800">Verification Approved, {name}!</h3>
        <p className="mt-3 text-gray-600">
          Your documents have been verified successfully. Your application is now in the final stage of approval. You will be able to access the dashboard shortly.
        </p>
        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className="rounded px-4 py-2 bg-gray-100 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </Backdrop>
  );
};

const ApplicationRejectedModal = ({ name, reason, onClose }) => {
  const navigate = useNavigate();
  return (
    <Backdrop onClose={onClose}>
      <div className="rounded-2xl bg-white p-6 shadow-xl border border-red-100">
        <h3 className="text-2xl font-semibold text-red-600">Application Rejected</h3>
        <p className="mt-3 text-gray-700">
          Dear {name}, we regret to inform you that your handyman application has been rejected.
        </p>
        {reason && (
          <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-100">
            <p className="text-sm font-semibold text-red-800">Reason for Rejection:</p>
            <p className="text-sm text-red-700 mt-1">{reason}</p>
          </div>
        )}
        <p className="mt-4 text-gray-600 text-sm">
          If you would like to edit your details or submit updated credentials, you can re-apply by clicking the button below.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => navigate("/handyman-onboarding")}
            className="rounded-lg px-4 py-2.5 bg-[#EFA938] hover:bg-[#d78c1f] text-white font-semibold transition-colors"
          >
            Re-Apply / Appeal
          </button>
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Backdrop>
  );
};

const RejectedDocumentsModal = ({ rejectedDocs = [], onClose, userId, refreshProfile }) => {
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setFiles({});
    setError(null);
    setSubmitting(false);
  }, [rejectedDocs]);

  const handleFileChange = (docType, file) => {
    setFiles((prev) => ({ ...prev, [docType]: file }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Check for missing files
    const missing = rejectedDocs.filter((d) => !files[d.documentType]);
    if (missing.length > 0) {
      setError(
        `Please upload files for the following documents: ${missing
          .map((m) => m.documentType)
          .join(", ")}`
      );
      return;
    }

    try {
      setSubmitting(true);

      // Build FormData
      const formData = new FormData();
      rejectedDocs.forEach((doc) => {
        const file = files[doc.documentType];
        let fieldName = "";

        switch (doc.documentType) {
          case "Profile Image":
            fieldName = "profileImage";
            break;
          case "CNIC Front":
            fieldName = "cnicFrontDocument";
            break;
          case "CNIC Back":
            fieldName = "cnicBackDocument";
            break;
          case "Certificate":
            fieldName = "certificationDocuments";
            break;
          default:
            console.warn("Unknown document type:", doc.documentType);
            return;
        }

        formData.append(fieldName, file);
        formData.append(`${fieldName}Id`, doc._id);
      });

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No auth token present.");

      const res = await fetch(`${process.env.REACT_APP_API_URL}/handyman/documents/${userId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Upload failed");
      }

      const json = await res.json();
      if (!json.success) {
        throw new Error(json.message || "Upload failed");
      }

      // ✅ Show success toast
      toast.success("Documents uploaded successfully");

      // ✅ Refresh profile so UI updates to "Pending" status
      if (refreshProfile) {
        await refreshProfile();
      }

      // ✅ Close modal
      onClose();

    } catch (err) {
      setSubmitting(false);
      setError(err?.message || "Upload failed. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative z-50 w-full max-w-3xl rounded-2xl overflow-hidden shadow-xl">
        {/* Header */}
        <div className="bg-[#EFA938] p-5">
          <h3 className="text-white text-2xl font-bold">
            Action Required — Documents Rejected
          </h3>
          <p className="text-white mt-1">
            Please re-upload the following rejected documents to access your dashboard.
          </p>
        </div>

        {/* Body */}
        <div className="bg-white p-6 max-h-[80vh] overflow-y-auto space-y-4">
          <ul className="space-y-3">
            {rejectedDocs.map((doc) => (
              <li key={doc._id} className="border rounded p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold">{doc.documentType}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Feedback: {doc.reviewFeedback || "No feedback provided"}
                    </p>
                    {doc.filePath && (
                      <a
                        href={doc.filePath}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                      >
                        View previous upload
                      </a>
                    )}
                  </div>
                  <div className="min-w-[220px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload corrected file
                    </label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleFileChange(doc.documentType, e.target.files[0])}
                      className="w-full"
                      disabled={submitting}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={handleSubmit}
              className="rounded px-4 py-2 bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f]"
              disabled={submitting}
            >
              {submitting ? "Uploading..." : "Upload Replaced Documents"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ---------------------- Main Dashboard ---------------------- */

const Dashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null); // entire API data
  const [dashboardData, setDashboardData] = useState(null); // dashboard stats and recent requests
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showFinalApprovalModal, setShowFinalApprovalModal] = useState(false);
  const [showRejectedApplicationModal, setShowRejectedApplicationModal] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [userId] = useState(localStorage.getItem("authUser")? JSON.parse(localStorage.getItem("authUser"))._id : null);

  const fetchData = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setApiError(null);
    const token = localStorage.getItem("authToken");
    if (!token) {
      setApiError("No auth token found. Please login.");
      setLoading(false);
      setIsRefreshing(false);
      return;
    }

    try {
      const [profileRes, dashboardRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_URL}/handyman/profile/${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_API_URL}/handyman/dashboard`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!profileRes.ok) {
        const text = await profileRes.text();
        throw new Error(text || "Failed to fetch profile");
      }

      const profileJson = await profileRes.json();
      if (!profileJson.success) {
        throw new Error(profileJson.message || "Failed to fetch profile");
      }
      setProfile(profileJson.data);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        if (dashboardJson.success) {
          setDashboardData(dashboardJson.data);
        }
      }
    } catch (err) {
      if (
        !err.message?.toLowerCase().includes("access denied") &&
        !err.message?.toLowerCase().includes("profile_not_found")
      ) {
        console.error("Fetch error:", err);
      }
      setApiError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleRefresh = async () => {
    await fetchData(true);
  };
  

  // derive documents and handyman values
  const documents = profile?.documents || [];
  const handyman = profile?.handyman || profile?.user ? profile?.handyman || profile?.user : null;
  const onboardingCompleted = handyman?.onboardingCompleted ?? false;
  // localStorage.setItem("onboardingCompleted", onboardingCompleted);

  const statuses = documents.map((d) => d.status);
  const allPending = documents.length > 0 && statuses.some((s) => s === "Pending");
  const hasRejected = documents.some((d) => d.status === "Rejected");
  const allApproved = documents.length > 0 && statuses.every((s) => s === "Approved") ;
  const overAllRejected = documents.length > 0 && statuses.every((s) => s === "Rejected") && profile?.handyman?.onboardingCompleted === false  ;

  // pick name to show (handyman.user.fullName if present, else profile.user.fullName)
  const displayName = profile?.handyman?.user?.fullName || profile?.user?.fullName || "Handyman";
  const welcomeName = dashboardData?.handymanName 

  // Rejected docs to pass to modal (with reviewFeedback)
  const rejectedDocs = documents.filter((d) => d.status === "Rejected");

  useEffect(() => {
  if (profile && handyman) {
    localStorage.setItem(
      "onboardingCompleted",
      String(handyman.onboardingCompleted)
    );

    // 🔔 notify layout
    window.dispatchEvent(new Event("onboarding-updated"));
  }
}, [profile, handyman]);


  // Effect to open the correct modal on fresh fetch
  useEffect(() => {
    if (!loading && profile) {
      if (handyman?.verification?.status === "Rejected") {
        setShowRejectedApplicationModal(true);
        setShowPendingModal(false);
        setShowRejectedModal(false);
        setShowFinalApprovalModal(false);
        return;
      }

      if (documents.length === 0) {
        // no documents uploaded - treat as pending (you can change this behavior)
        setShowPendingModal(true);
        setShowRejectedModal(false);
        setShowFinalApprovalModal(false);
        setShowRejectedApplicationModal(false);
        return;
      }

      if (allPending) {
        setShowPendingModal(true);
        setShowRejectedModal(false);
        setShowFinalApprovalModal(false);
        setShowRejectedApplicationModal(false);
        return;
      }

      if (hasRejected) {
        setShowRejectedModal(true);
        setShowPendingModal(false);
        setShowFinalApprovalModal(false);
        setShowRejectedApplicationModal(false);
        return;
      }

      if (allApproved && !onboardingCompleted) {
        setShowFinalApprovalModal(true);
        setShowRejectedModal(false);
        setShowPendingModal(false);
        setShowRejectedApplicationModal(false);
        return;
      }

      // Otherwise: allApproved && onboardingCompleted OR any other allowed case -> show dashboard normally
      setShowRejectedModal(false);
      setShowPendingModal(false);
      setShowFinalApprovalModal(false);
      setShowRejectedApplicationModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profile]);

  // default upload implementation (you can replace it with your own)
  const uploadRejectedDocuments = async (filesMap) => {
    // filesMap: { [documentId]: File }
    // we will POST to a sample endpoint; replace URL with your real endpoint
    const token = localStorage.getItem("authToken");
    if (!token) throw new Error("No auth token present.");
    const user = localStorage.getItem("authUser")

    const formData = new FormData();
    Object.entries(filesMap).forEach(([docId, file]) => {
      // key by documentId so backend knows which doc is being replaced
      formData.append("files", file, file.name);
      formData.append("documentIds[]", docId);
    });

    const res = await fetch(`${process.env.REACT_APP_API_URL}/handyman/documents/${userId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData, // send FormData directly
    }
  );

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Upload failed");
  }

  const json = await res.json();
  if (!json.success) {
    throw new Error(json.message || "Upload failed");
  }

  // Re-fetch profile to refresh statuses
  await refreshProfile();

  return json;
};



  const refreshProfile = async () => {
    // minimal re-fetch logic so UI updates after upload
    setLoading(true);
    setApiError(null);
    const token = localStorage.getItem("authToken");
    if (!token) {
      setApiError("No auth token found. Please login.");
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/handyman/profile/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Failed to refresh");
      setProfile(json.data);
    } catch (err) {
      console.error("refresh error:", err);
      setApiError(err.message || "Failed to refresh profile");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- UI Rendering ---------------------- */

  if (loading) {
    return <p className="p-6 text-gray-600">Loading dashboard...</p>;
  }

  if (apiError) {
    let isAccessDenied = false;
    let isProfileNotFound = false;
    try {
      const parsed = JSON.parse(apiError);
      if (parsed.message && parsed.message.toLowerCase().includes("access denied")) {
        isAccessDenied = true;
      }
      if (
        parsed.error?.code === "PROFILE_NOT_FOUND" ||
        (parsed.error?.message && parsed.error.message.toLowerCase().includes("handyman profile not found")) ||
        (parsed.message && parsed.message.toLowerCase().includes("handyman profile not found"))
      ) {
        isProfileNotFound = true;
      }
    } catch (e) {
      if (apiError.toLowerCase().includes("access denied")) {
        isAccessDenied = true;
      }
      if (apiError.toLowerCase().includes("profile_not_found") || apiError.toLowerCase().includes("handyman profile not found")) {
        isProfileNotFound = true;
      }
    }

    if (isProfileNotFound) {
      return (
        <div className="min-h-[80vh] flex items-center justify-center p-4 md:p-8 bg-gray-50 dark:bg-slate-900 transition-colors duration-300">
          <div className="relative w-full max-w-xl bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700/80 rounded-3xl p-8 md:p-10 text-center overflow-hidden">
            
            {/* Background absolute decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#EFA938]/10 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#EFA938]/5 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none" />

            {/* Glowing Icon Container */}
            <div className="relative mx-auto w-20 h-20 flex items-center justify-center bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-full shadow-inner mb-6 transition-all duration-500 hover:scale-105">
              <Briefcase className="h-10 w-10 text-[#EFA938] stroke-[1.5]" />
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FFF8EF] dark:bg-[#EFA938]/10 text-[#EFA938] rounded-full text-xs font-semibold uppercase tracking-wider mb-4 border border-[#EFA938]/10">
              Profile Required
            </div>

            {/* Typography */}
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Handyman Profile Not Found
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm md:text-base max-w-md mx-auto">
              It looks like you haven't completed your handyman registration yet. To access the handyman dashboard, please complete the onboarding process.
            </p>

            {/* Support Info Box */}
            <div className="my-8 p-4 bg-[#FFF8EF]/50 dark:bg-[#EFA938]/5 rounded-2xl border border-[#EFA938]/10 flex flex-col sm:flex-row items-center justify-around gap-4 text-sm text-gray-600 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#EFA938]" />
                <span className="font-semibold">+92 3039672114</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-gray-200 dark:bg-slate-700" />
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#EFA938]" />
                <span className="font-semibold">support@example.com</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/handyman-onboarding')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#EFA938] hover:bg-[#d8962c] text-white font-semibold shadow-md shadow-[#EFA938]/20 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Complete Onboarding
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300 font-semibold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <ArrowLeft className="h-4 w-4" />
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (isAccessDenied) {
      // Get user name from authUser in localStorage
      let cachedName = "Handyman";
      try {
        const authUserObj = localStorage.getItem("authUser") ? JSON.parse(localStorage.getItem("authUser")) : null;
        if (authUserObj) {
          cachedName = authUserObj.fullName || authUserObj.name || authUserObj.email || "Handyman";
        }
      } catch (e) {
        console.error("Error reading user name from localStorage:", e);
      }

      return (
        <div className="min-h-[80vh] flex items-center justify-center p-4 md:p-8 bg-gray-50 dark:bg-slate-900 transition-colors duration-300">
          <div className="relative w-full max-w-xl bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700/80 rounded-3xl p-8 md:p-10 text-center overflow-hidden">
            
            {/* Background absolute decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#EFA938]/10 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#EFA938]/5 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none" />

            {/* Glowing Icon Container */}
            <div className="relative mx-auto w-20 h-20 flex items-center justify-center bg-[#FFF8EF] dark:bg-[#EFA938]/10 rounded-full shadow-inner mb-6 transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-[#EFA938]/10 rounded-full animate-ping opacity-25" style={{ animationDuration: '3s' }} />
              <ShieldAlert className="h-10 w-10 text-[#EFA938] stroke-[1.5]" />
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#FFF8EF] dark:bg-[#EFA938]/10 text-[#EFA938] rounded-full text-xs font-semibold uppercase tracking-wider mb-4 border border-[#EFA938]/10">
              <Clock className="h-3.5 w-3.5 animate-pulse" />
              Application Under Review
            </div>

            {/* Typography */}
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Verifying Your Account
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm md:text-base max-w-md mx-auto">
              Welcome back, <span className="font-semibold text-gray-800 dark:text-gray-200">{cachedName}</span>. 
              Our team is currently reviewing your handyman onboarding application and documents.
            </p>

            {/* Verification progress steps */}
            <div className="my-8 py-6 px-5 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-800/80 text-left space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-7 w-7 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Onboarding Submission</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Onboarding forms and files submitted successfully</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-7 w-7 rounded-full bg-[#FFF8EF] dark:bg-[#EFA938]/10 flex items-center justify-center text-[#EFA938]">
                  <Clock className="h-4 w-4 animate-spin" style={{ animationDuration: '4s' }} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    Document Verification
                    <span className="text-[10px] bg-yellow-100 dark:bg-yellow-950 px-1.5 py-0.5 rounded text-yellow-800 dark:text-yellow-400 font-normal">Active</span>
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Our support team is checking your profile & ID details</p>
                </div>
              </div>

              <div className="flex items-center gap-3 opacity-50">
                <div className="h-7 w-7 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400">
                  <Lock className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Dashboard Activation</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Unlock shifts, jobs, earnings, and scheduling tools</p>
                </div>
              </div>
            </div>

            {/* Support Info Box */}
            <div className="mb-8 p-4 bg-[#FFF8EF]/50 dark:bg-[#EFA938]/5 rounded-2xl border border-[#EFA938]/10 flex flex-col sm:flex-row items-center justify-around gap-4 text-sm text-gray-600 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#EFA938]" />
                <span className="font-semibold">+92 3039672114</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-gray-200 dark:bg-slate-700" />
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#EFA938]" />
                <span className="font-semibold">support@example.com</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => handleRefresh()}
                disabled={isRefreshing}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#EFA938] hover:bg-[#d8962c] text-white font-semibold shadow-md shadow-[#EFA938]/20 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-75 disabled:pointer-events-none"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Checking Status...' : 'Refresh Status'}
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300 font-semibold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <ArrowLeft className="h-4 w-4" />
                Go to Homepage
              </button>
            </div>
            
            <p className="text-[11px] text-gray-400 mt-6">
              Thank you for your patience. Standard review time takes up to 48 hours.
            </p>
          </div>
        </div>
      );
    }

    // Default error display for other types of errors
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/50 rounded-2xl p-6 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-red-800 dark:text-red-300">An Error Occurred</h3>
          <p className="text-sm text-red-600 dark:text-red-400 mt-2">{apiError}</p>
          <button
            onClick={() => fetchData()}
            className="mt-5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // CASE D: allowed to see dashboard when all docs approved and onboarding done
  const canSeeDashboard = allApproved && onboardingCompleted;

  return (
    <div className="relative">
      {/* Modals based on state */}
      {showPendingModal && (
        <PendingReviewModal
          name={displayName}
          onClose={() => setShowPendingModal(false)}
        />
      )}

     {showRejectedModal && (
        <RejectedDocumentsModal
            rejectedDocs={rejectedDocs}
            onClose={() => setShowRejectedModal(false)}
            userId={userId}
            refreshProfile={refreshProfile} // pass the refetch function from Dashboard
        />
        )}

      {showFinalApprovalModal && (
        <FinalApprovalPendingModal
          name={displayName}
          onClose={() => setShowFinalApprovalModal(false)}
        />
      )}

      {showRejectedApplicationModal && (
        <ApplicationRejectedModal
          name={displayName}
          reason={handyman?.rejectionReason}
          onClose={() => setShowRejectedApplicationModal(false)}
        />
      )}

      {/* If not allowed to see dashboard content, show a subtle disabled/blurred overlay
          (we already show modal above for blocking cases; keep dashboard content but blurred behind) */}
      <div className={`${(showPendingModal || showRejectedModal || showFinalApprovalModal || showRejectedApplicationModal) ? "filter blur-sm pointer-events-none select-none" : ""}`}>
        {/* ===== Header ===== */}
        <div className="p-6 space-y-8">
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600 mt-1">Welcome back, {welcomeName}</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-2 text-sm text-gray-600">
                <CalendarClock className="h-4 w-4 text-[#EFA938]" />
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* ===== Stats ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {(() => {
              const counts = dashboardData?.counts || {
                total: 0,
                pending: 0,
                active: 0,
                completed: 0,
                scheduled: 0,
                cancelled: 0,
                disputed: 0,
              };
              const earnings = dashboardData?.earnings || {
                totalEarnings: 0,
                completedJobs: 0,
              };
              const statsArr = [
                { title: "Total Jobs", value: counts.total, description: "Total jobs assigned", icon: Briefcase },
                { title: "Earnings", value: `Rs ${earnings.totalEarnings}`, description: "Total earnings", icon: Wallet },
                { title: "Active", value: counts.active, description: "Active jobs", icon: Zap },
                { title: "Completed", value: counts.completed, description: "Completed jobs", icon: CheckCircle2 },
                { title: "Scheduled", value: counts.scheduled, description: "Scheduled jobs", icon: CalendarClock },
                { title: "Cancelled", value: counts.cancelled, description: "Cancelled jobs", icon: XCircle },
                { title: "Disputed", value: counts.disputed, description: "Disputed jobs", icon: AlertCircle },
              ];
              return statsArr.map((stat) => (
                <div key={stat.title} className="rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{stat.title}</p>
                      <p className="mt-1 text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className="h-11 w-11 rounded-xl bg-[#FFF8EF] flex items-center justify-center">
                      <stat.icon className="h-5 w-5 text-[#EFA938]" />
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-500">{stat.description}</p>
                </div>
              ));
            })()}
          </div>

          {/* ===== Recent Requests Section ===== */}
          <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Recent Requests</h2>
              <button
                onClick={() => navigate("/handyman/my-jobs")}
                className="text-sm font-semibold text-[#EFA938] hover:underline"
              >
                View all
              </button>
            </header>

            <div className="divide-y divide-gray-100">
              {dashboardData?.recentRequests && dashboardData.recentRequests.length > 0 ? (
                dashboardData.recentRequests.map((request) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'active':
                        return 'bg-blue-100 text-blue-700';
                      case 'completed':
                        return 'bg-green-100 text-green-700';
                      case 'scheduled':
                        return 'bg-purple-100 text-purple-700';
                      case 'pending':
                        return 'bg-yellow-100 text-yellow-700';
                      default:
                        return 'bg-gray-100 text-gray-700';
                    }
                  };

                  const formatDate = (dateString) => {
                    if (!dateString) return '';
                    const date = new Date(dateString);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  };

                  return (
                    <div key={request.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">{request.serviceName}</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            </span>
                          </div>
                          {request.subServices && request.subServices.length > 0 && (
                            <p className="text-sm text-gray-500 mt-1">
                              {request.subServices.join(', ')}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 mt-1">Customer: {request.customerName}</p>
                          {request.scheduleDate && (
                            <p className="text-sm text-gray-500 mt-1">
                              Scheduled: {formatDate(request.scheduleDate)} {request.scheduleTime ? `at ${request.scheduleTime}` : ''}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-xs text-gray-500 whitespace-nowrap pt-1">
                          {formatDate(request.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  <p>No recent requests found</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* If the user can't see dashboard, display a small notice (optional) */}
      {!canSeeDashboard && (
        <div className="fixed bottom-6 right-6 z-30">
          <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3">
            <p className="text-sm text-yellow-800">
             Your profile is pending admin approval. Dashboard access will be granted once approved.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
